/**
 * Distorts points towards a configurable square frame shape with noise
 * @param {number} x - X coordinate
 * @param {number} y - Y coordinate
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @param {number} edgeDistance - Controls how close to the edge the distortion reaches (0-1)
 * @param {number} pullStrength - Strength of the distortion effect
 * @param {number} time - Animation time for pulsing effect
 * @returns {Object} Distorted point {x, y}
 */
export const squareDistortion = (
  x,
  y,
  width,
  height,
  edgeDistance = 0.7,
  pullStrength = 1.0,
  time = 0
) => {
  // Return original if either x or y is undefined
  if (x === undefined || y === undefined) return { x, y };

  const centerX = width / 2;
  const centerY = height / 2;

  // Calculate normalized position relative to center
  const normX = (x - centerX) / centerX;
  const normY = (y - centerY) / centerY;

  // Calculate distance from center as a max norm (square shape)
  const centerDist = Math.max(Math.abs(normX), Math.abs(normY));
  const reverseCenterDist = 1 - centerDist;


  // Calculate pull factor - 1 at center, 0 at threshold
  const pullFactor = Math.max(0, pullStrength * (1 - centerDist / edgeDistance));

  // Clamp the pull strength to avoid excessive displacement
  const maxPull = 1;
  const pullAmount = Math.min(Math.pow(pullFactor, 1.5), maxPull);

  let newX = x;
  let newY = y;

  // Apply quadrant-based pull with focus on primary direction
  if (Math.abs(normX) > Math.abs(normY)) {
    // Horizontal pull is dominant
    if (normX < 0) {
      // Left side - pull left
      newX = x - pullAmount * centerX * reverseCenterDist * reverseCenterDist;
    } else {
      // Right side - pull right
      newX = x + pullAmount * centerX * reverseCenterDist * reverseCenterDist;
    }
  } else {
    // Vertical pull is dominant
    if (normY < 0) {
      // Top side - pull up
      newY = y - pullAmount * centerY * reverseCenterDist * reverseCenterDist;
    } else {
      // Bottom side - pull down
      newY = y + pullAmount * centerY * reverseCenterDist * reverseCenterDist;
    }
  }


  const staticRandomPlusMinus = (Math.abs(Math.sin(x * 12.9898 + y * 78.233) * 43758.5453) % 1) * 2 - 1;

  // Create random position offset for each particle
  const randomOffsetX = (Math.abs(Math.sin(x * 45.123 + y * 67.890) * 23456.789) % 1) * 60 - 30;
  const randomOffsetY = (Math.abs(Math.sin(x * 89.456 + y * 34.567) * 87654.321) % 1) * 60 - 30;

  // Create a unique time offset for each particle based on position
  const timeOffset = (Math.abs(Math.sin(x * 78.233 + y * 12.9898) * 43758.5453) % 1) * Math.PI * 2;

  // Use sine wave with particle-specific offset for smooth pulsing
  const pulse = Math.sin(time * 0.08 + timeOffset) * 30;

  newX += staticRandomPlusMinus * 50 + pulse + randomOffsetX;
  newY += staticRandomPlusMinus * 50 + pulse + randomOffsetY;

  return { x: newX, y: newY };
};
